import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Upload, Users, Sparkles, Lock, Mail, Tag } from "lucide-react";

const parseEmails = (text) =>
  Array.from(new Set(
    (text || "")
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e))
  ));

export default function MailerPage() {
  const [raw, setRaw] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [buttonLabel, setButtonLabel] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [stores, setStores] = useState([]);
  const [applying, setApplying] = useState(false);
  const fileRef = useRef();

  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)).catch(() => {}); }, []);

  const emails = parseEmails(raw);
  const origin = window.location.origin;
  const protectedStores = stores.filter((s) => s.password_protected && s.access_password);

  const onFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => setRaw((prev) => (prev ? prev + "\n" : "") + reader.result);
    reader.readAsText(file);
  };

  const newsletterTemplate = () => {
    setSubject("What's new this week ✨");
    setBody("Hi there,\n\nHere's the latest from our stores — fresh drops, restocks and a few things we think you'll love.\n\nThanks for being part of the community!");
    setButtonLabel("Shop new arrivals");
    setButtonUrl(origin);
    toast.success("Newsletter template applied");
  };

  const specialOfferTemplate = () => {
    setSubject("A special offer, just for you 🎁");
    setBody("Hi there,\n\nFor a limited time, enjoy an exclusive discount on your next order. Use it before it's gone!\n\nHappy shopping.");
    setButtonLabel("Claim your offer");
    setButtonUrl(origin);
    toast.success("Special offer template applied");
  };

  const inviteTemplate = async (store) => {
    setApplying(true);
    let link = "";
    try {
      if (store) {
        const existing = await api.get(`/stores/${store.id}/access-tokens`);
        const active = (existing.data || []).find((t) => !t.revoked);
        const tok = active || (await api.post(`/stores/${store.id}/access-tokens`, { label: "Email invite" })).data;
        link = `${origin}/${store.handle}?token=${tok.token}`;
      }
      setSubject(store ? `You're invited to ${store.name} — a private store 🔒` : "You're invited to a private store 🔒");
      setBody(
        `Hi there,\n\nYou've been given exclusive early access to ${store ? store.name : "our private store"}.\n` +
        `Tap the button below to unlock the store — the link does it for you, no password needed.\n\nSee you inside!`
      );
      setButtonLabel("Enter the store");
      if (link) setButtonUrl(link);
      toast.success(store ? "Invite link created & template applied" : "Invite template applied");
    } catch {
      toast.error("Could not create the access link");
    } finally { setApplying(false); }
  };

  const addAudience = async (kind) => {
    try {
      const res = await api.get(`/admin/audience/${kind}`);
      const list = res.data.emails || [];
      if (!list.length) { toast.message(`No ${kind} emails found yet`); return; }
      setRaw((prev) => (prev ? prev + "\n" : "") + list.join("\n"));
      toast.success(`Added ${list.length} ${kind === "newsletter" ? "subscribers" : "customers"}`);
    } catch { toast.error("Could not load audience"); }
  };

  const send = async () => {
    if (emails.length === 0) { toast.error("Add at least one valid recipient email"); return; }
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    if (!body.trim()) { toast.error("Message body is required"); return; }
    setSending(true);
    try {
      const res = await api.post("/admin/mailer/send", {
        recipients: emails, subject, body,
        button_label: buttonLabel, button_url: buttonUrl, origin_url: origin,
      });
      toast.success(`Sent to ${res.data.sent} recipient${res.data.sent === 1 ? "" : "s"}${res.data.tracked ? " · clicks tracked" : ""}${res.data.invalid_count ? ` · ${res.data.invalid_count} invalid skipped` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="animate-fade-up max-w-3xl pb-16" data-testid="mailer-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Marketing</span>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-1">Mass mailer</h1>
          <p className="text-zinc-500 font-body mt-1">Paste or upload a CSV of emails, compose your message, and send a branded broadcast. Links are click-tracked.</p>
        </div>
        <button onClick={send} disabled={sending} data-testid="mailer-send" className="casper-btn rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send ({emails.length})
        </button>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-1">
          <Label>Recipients</Label>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500 flex items-center gap-1" data-testid="mailer-count"><Users size={13} /> {emails.length} valid</span>
            <button onClick={() => addAudience("newsletter")} className="flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50" data-testid="mailer-add-subscribers">
              <Mail size={12} /> Subscribers
            </button>
            <button onClick={() => addAudience("customers")} className="flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50" data-testid="mailer-add-customers">
              <Users size={12} /> Customers
            </button>
            <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" hidden onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} data-testid="mailer-csv-input" />
            <button onClick={() => fileRef.current.click()} className="flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50" data-testid="mailer-csv-upload">
              <Upload size={12} /> Upload CSV
            </button>
          </div>
        </div>
        <textarea
          data-testid="mailer-recipients"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={5}
          placeholder="Paste emails separated by commas, spaces or new lines — or upload a CSV."
          className="fin font-mono text-xs"
        />
      </Card>

      <Card>
        <Label>Templates</Label>
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <button onClick={newsletterTemplate} data-testid="mailer-template-newsletter" className="flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50">
            <Mail size={12} /> Newsletter update
          </button>
          <button onClick={specialOfferTemplate} data-testid="mailer-template-offer" className="flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50">
            <Tag size={12} /> Special offer
          </button>
          <button onClick={() => inviteTemplate(null)} disabled={applying} data-testid="mailer-template-invite" className="flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50 disabled:opacity-60">
            <Sparkles size={12} /> Invite to private store
          </button>
          {protectedStores.map((s) => (
            <button key={s.id} onClick={() => inviteTemplate(s)} disabled={applying} data-testid={`mailer-template-store-${s.handle}`} className="flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 hover:bg-zinc-50 disabled:opacity-60">
              {applying ? <Loader2 size={11} className="animate-spin" /> : <Lock size={11} />} Invite to {s.name}
            </button>
          ))}
        </div>
        {protectedStores.length === 0 && (
          <p className="text-xs text-zinc-400 mt-2">Tip: enable “Members only” on a store to auto-generate a revocable unlock link for invites.</p>
        )}
      </Card>

      <Card>
        <Label>Subject</Label>
        <input data-testid="mailer-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="fin mb-4" placeholder="Your subject line" />
        <Label>Message</Label>
        <textarea data-testid="mailer-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="fin mb-4" placeholder={"Write your message.\nNew lines become paragraphs."} />
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Button label (optional)</Label><input data-testid="mailer-button-label" value={buttonLabel} onChange={(e) => setButtonLabel(e.target.value)} className="fin" placeholder="Shop now" /></div>
          <div><Label>Button link (optional, tracked)</Label><input data-testid="mailer-button-url" value={buttonUrl} onChange={(e) => setButtonUrl(e.target.value)} className="fin" placeholder="https://..." /></div>
        </div>
      </Card>

      <button onClick={send} disabled={sending} data-testid="mailer-send-bottom" className="casper-btn rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to {emails.length} recipient{emails.length === 1 ? "" : "s"}
      </button>

      <style>{`.fin{width:100%;border:1px solid #e4e4e7;border-radius:12px;padding:10px 14px;font-size:14px;outline:none;font-family:'Plus Jakarta Sans'}.fin:focus{border-color:#4F46E5}`}</style>
    </div>
  );
}

const Card = ({ children }) => (
  <div className="bg-white border border-zinc-200/80 rounded-xl p-6 mb-6">{children}</div>
);
const Label = ({ children }) => (
  <label className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 block mb-1.5">{children}</label>
);
